
import {
  computed, defineComponent, inject, onMounted, onUnmounted, ref, watch,
} from 'vue';
import RefreshPull from 'pull-refresh-vue3';
import { useRouter } from 'vue-router';
import {
  APPLICATION_SETTINGS_TYPE,
  CONTACT_STATUS,
  GROUP_TYPE,
  GROUP_USER_STATUS,
  NETWORK_CONTACT_TYPES,
  NOTIFICATION_READ_MODE,
  NOTIFICATION_TYPE,
} from '@/types/enums';
// eslint-disable-next-line import/no-cycle
import { useMainStore } from '@/store/mainStore';
// eslint-disable-next-line import/no-cycle
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';
// eslint-disable-next-line import/no-cycle
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
// eslint-disable-next-line import/no-cycle
import MonitoringService from '@/services/MonitoringService';
// eslint-disable-next-line import/no-cycle
import NativeService from '@/services/NativeService';
// eslint-disable-next-line import/no-cycle
import UserService from '@/services/UserService';
import MessageGroupChatInfo from '@/modules/messages/components/MessageGroupChatInfo.vue';
import UsePullToRefresh from '@/composables/UsePullToRefresh';
import AppItemWrapper from '@/components/AppItemWrapper.vue';
import SpacesMyGroups from '@/modules/spaces/components/SpacesMyGroups.vue';
import SpacesInvitations from '@/modules/spaces/components/SpacesInvitations.vue';
import SpacesOutcoming from '@/modules/spaces/components/SpacesOutcoming.vue';
import SpacesGroup from '@/modules/spaces/components/SpacesGroup.vue';
import {
  IConversationItemGroupList, IGroupData, INotificationRead,
} from '@/types/interfaces';
import AppNoMessage from '@/components/AppNoMessage.vue';

export default defineComponent({
  components: {
    AppNoMessage,
    SpacesInvitations,
    // SpacesSuggestions,
    SpacesOutcoming,
    RefreshPull,
    SpacesGroup,
    AppItemWrapper,
    SpacesMyGroups,
    MessageGroupChatInfo,
  },
  setup() {
    const spacesStore = useSpacesStore();
    const mainStore = useMainStore();
    const messagesStore = useMessagesStore();
    const router = useRouter();

    const isGroupInfo = ref(false);
    // eslint-disable-next-line @typescript-eslint/ban-types
    const $localize = inject('$localize') as Function;

    const filteredInvitationsAndRequest = computed(() => [...spacesStore.filteredInvitationsGroup, ...spacesStore.filteredRequestsToMyGroups]);

    const myGroupsSearch = computed(() => {
      const arr = spacesStore.searchGroupsList.filter(
        (searchGroup) => spacesStore.myGroups.some((myGroup) => searchGroup.id === myGroup.id),
      );
      return arr;
    });

    const publicGroupsSearch = computed(() => {
      const arr = spacesStore.searchGroupsList.filter(
        (searchGroup) => searchGroup.groupType === GROUP_TYPE.PUBLIC_OPEN && !searchGroup.members.find((member) => member.accountId === mainStore.user.accountId),
      );
      return arr;
    });

    const myGroups = computed(() => spacesStore.myGroups.length);

    const noData = computed(() => {
      const isSearchDataEmpty = !myGroupsSearch.value.length && !publicGroupsSearch.value.length;
      const isDataEmpty = !myGroups.value && !filteredInvitationsAndRequest.value.length && !spacesStore.filteredOutgoingGroup.length;

      return spacesStore.search ? isSearchDataEmpty : isDataEmpty;
    });

    const noDataMessage = computed(() => ({
      text: $localize('no.search.results'),
      icon: 'icon-not-found',
    }));
    const isManageContacts = computed(
      () => mainStore.applicationSettings.find((feature) => feature.name === APPLICATION_SETTINGS_TYPE.CONTACT_NETWORKING_FEATURE)?.enabled,
    );

    function shareApp(): void {
      if (mainStore.isNative) {
        NativeService.shareApp();
      }
    }

    function refreshContacts() {
      return Promise.all([spacesStore.fetchInvitationsGroup(),
        spacesStore.fetchRequestsToMyGroups(),
        spacesStore.fetchOutgoingGroup(),
        spacesStore.fetchMyGroups(),
        spacesStore.fetchUsersGroups()]);
    }

    const {
      loadingPull, loosingText, successText, pullingText, loadingText, onRefresh,
    } = UsePullToRefresh(refreshContacts);

    // search logic
    function searchGroups(query: string): void {
      spacesStore.searchGroups(query);
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => spacesStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      spacesStore.dataLoading = true;
      if (newSearch === '') {
        spacesStore.searchGroupsList = [];
      } else {
        timeout = setTimeout(() => {
          searchGroups(newSearch);
        }, 300);
      }
    });

    function createGroup() {
      router.push('/messages/my?createGroup=true');
    }

    function openGroupInfo(group: IGroupData) {
      // set data to store
      const obj: IConversationItemGroupList = {
        conversationId: '',
        group,
        lastMessageSentByOwner: false,
        lastMessageTimestamp: null,
        previewText: null,
        unreadCount: 0,
      };

      messagesStore.currentConversation = messagesStore.conversationGroupListToConversation(obj);
      isGroupInfo.value = true;
    }

    function closeGroupInfo() {
      messagesStore.currentConversation = null;
      isGroupInfo.value = false;
    }

    function readNotification() {
      const payload: INotificationRead[] = [];
      if (mainStore.badgesGroup) {
        payload.push({
          type: NOTIFICATION_TYPE.GROUP_REQUEST_SENT,
          mode: NOTIFICATION_READ_MODE.BY_TYPE,
        });
        mainStore.badgesAlert -= mainStore.badgesGroup;
        mainStore.badgesGroup = 0;
      }
      if (mainStore.badgesInGroup) {
        payload.push({
          type: NOTIFICATION_TYPE.IN_GROUP_REQUEST_SENT,
          mode: NOTIFICATION_READ_MODE.BY_TYPE,
        });
        mainStore.badgesAlert -= mainStore.badgesInGroup;
        mainStore.badgesInGroup = 0;
      }
      if (payload.length) {
        UserService.readNotification(payload);
      }
    }

    watch(() => filteredInvitationsAndRequest.value.length, () => {
      readNotification();
    });

    onMounted(() => {
      MonitoringService.sendPageMountedSpeed();
      readNotification();
    });

    onUnmounted(() => {
      spacesStore.search = '';
    });

    return {
      isManageContacts,
      noData,
      spacesStore,
      NETWORK_CONTACT_TYPES,
      shareApp,

      loadingPull,
      loosingText,
      successText,
      pullingText,
      loadingText,
      onRefresh,

      // resolveUserType,
      mainStore,
      myGroupsSearch,
      publicGroupsSearch,
      myGroups,
      filteredInvitationsAndRequest,
      createGroup,
      isGroupInfo,
      openGroupInfo,
      closeGroupInfo,
      CONTACT_STATUS,
      noDataMessage,
    };
  },
});
